import type { StructuralRangeKind } from '../types/structuralRangeTypes'

export const STRUCTURAL_RANGE_KIND_LABELS: Record<StructuralRangeKind, string> = {
  frame_dialogue: 'Frame dialogue',
  subframe: 'Subframe',
  scene: 'Scene',
  discourse: 'Dialogue',
  monologue: 'Monologue',
  subtale: 'Subtale',
  tertiary_tale: 'Tertiary tale',
  hikma: 'Ḥikma',
  mathal: 'Mathal',
  narrative: 'Narrative',
  divider: 'Divider',
  frame: 'Frame',
  tale: 'Tale',
  transition: 'Transition',
}

export const STRUCTURAL_RANGE_KIND_HELP: Record<StructuralRangeKind, string> = {
  frame_dialogue: 'An exchange in the framing narrative, including a resumed frame dialogue.',
  subframe: 'An embedded framing situation.',
  scene: 'A bounded episode that may contain a dialogue or monologue.',
  discourse: 'An exchange between speakers, including its embedded tales and maxims.',
  monologue: 'One speaker’s reflection or address to self, not an exchange.',
  subtale: 'A story embedded in a dialogue, frame, or surrounding narrative.',
  tertiary_tale: 'A story embedded within a subtale, including an episode witnessed by its protagonist.',
  hikma: 'A maxim or piece of wisdom, which may contain short analogies.',
  mathal: 'A short exemplum or analogy; use Subtale for a developed embedded story.',
  narrative: 'Connecting narrated action, distinguished from spoken dialogue.',
  divider: 'A structural heading anchored to existing rows; creates no empty text row.',
  frame: 'Existing outer narrative enclosure; not automatically a spoken frame dialogue.',
  tale: 'Existing general story classification; reclassify as Subtale or Tertiary tale when appropriate.',
  transition: 'Existing short connective passage; cannot contain child ranges.',
}

const newKinds: StructuralRangeKind[] = [
  'frame_dialogue', 'monologue', 'subtale', 'tertiary_tale',
  'hikma', 'mathal', 'narrative', 'divider',
]
export const requiresStructuralTaxonomyV2 = (kind: StructuralRangeKind | null): boolean => (
  kind !== null && newKinds.includes(kind)
)

const embedded: StructuralRangeKind[] = ['subframe', 'tale', 'subtale', 'tertiary_tale']
const speechContent: StructuralRangeKind[] = [...embedded, 'hikma', 'mathal', 'narrative', 'divider']
const narrativeContent: StructuralRangeKind[] = [
  ...speechContent, 'scene', 'discourse', 'transition', 'monologue', 'frame_dialogue',
]

// Preserve every v1 parent/child decision. New speech categories do not add
// narrative depth; only embedded framing/story categories do.
const allowedChildren: Record<StructuralRangeKind, ReadonlySet<StructuralRangeKind>> = {
  frame: new Set(narrativeContent),
  subframe: new Set(narrativeContent),
  tale: new Set(narrativeContent),
  scene: new Set<StructuralRangeKind>([...speechContent, 'discourse', 'transition', 'monologue', 'frame_dialogue']),
  discourse: new Set(speechContent),
  transition: new Set(),
  frame_dialogue: new Set<StructuralRangeKind>([...speechContent, 'discourse', 'monologue']),
  monologue: new Set(speechContent),
  subtale: new Set(narrativeContent),
  tertiary_tale: new Set(narrativeContent.filter(kind => kind !== 'subtale')),
  hikma: new Set<StructuralRangeKind>(['mathal']),
  mathal: new Set(),
  narrative: new Set(),
  divider: new Set(),
}

export const isStructuralChildKindAllowed = (
  parent: StructuralRangeKind,
  child: StructuralRangeKind,
): boolean => allowedChildren[parent]?.has(child) ?? false

export const structuralNarrativeIncrement = (kind: StructuralRangeKind | null): number => (
  kind !== null && embedded.includes(kind) ? 1 : 0
)
